// UploadFile.js
import axios from "axios";
import React, { useEffect, useMemo, useState } from "react";
import DisplayExcel from "./DisplayExcel";
import { getCookie } from "../../services/DataRequestService";
import { displayMessage } from "../../Utils/helper";
import AuthService from "../../services/AuthService";
import * as XLSX from "xlsx";
import { FaCloudUploadAlt } from "react-icons/fa";
import Loader from "../common/Loader";
import PageHeader from "../common/PageHeader";

const { REACT_APP_API_BASE_URL } = process.env;
const ROWS_PER_PAGE = 12;

const authHeaders = (multipart = false) => ({
  Accept: "application/json",
  "Content-Type": multipart ? "multipart/form-data" : "application/json",
  Authorization: AuthService.getAccessToken(),
  access_token: AuthService.getAccessToken(),
});

const UploadFile = () => {
  const [workflows, setWorkflows] = useState([]);
  const [selectedWorkflowID, setSelectedWorkflowID] = useState("");
  const [selectedWorkflowName, setSelectedWorkflowName] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [showTable, setShowTable] = useState(false);
  const [manualData, setManualData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");

  useEffect(() => {
    getWorkflows();
  }, []);

  const filteredWorkflows = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return workflows;
    return workflows.filter((wf) =>
      String(wf.workflow_name || "").toLowerCase().includes(term)
    );
  }, [workflows, search]);

  const totalPages = Math.max(1, Math.ceil(filteredWorkflows.length / ROWS_PER_PAGE));
  const safePage = Math.min(currentPage, totalPages);
  const pageStart = (safePage - 1) * ROWS_PER_PAGE;
  const pageWorkflows = useMemo(
    () => filteredWorkflows.slice(pageStart, pageStart + ROWS_PER_PAGE),
    [filteredWorkflows, pageStart]
  );

  const getWorkflows = async () => {
    try {
      const url = `${REACT_APP_API_BASE_URL}/get_wf_for_sp?user_id=${getCookie("sub")}`;
      const response = await axios.get(url, { headers: authHeaders() });
      setWorkflows(response.data.data || []);
      setCurrentPage(1);
    } catch (error) {
      console.error("Error fetching workflows:", error.message);
    }
  };

  const clearSelection = () => {
    setSelectedWorkflowID("");
    setSelectedWorkflowName("");
    setSelectedFile(null);
    setManualData([]);
    setShowTable(false);
  };

  const selectWorkflow = (workflow) => {
    if (!workflow) return;
    if (selectedWorkflowID === workflow.workflow_id) {
      clearSelection();
      return;
    }
    setSelectedWorkflowID(workflow.workflow_id);
    setSelectedWorkflowName(workflow.workflow_name);
    setSelectedFile(null);
    setManualData([]);
    setShowTable(false);
    fetchManualData(workflow.workflow_id, workflow.workflow_name);
  };

  const handleFileChange = (e, workflow) => {
    e.stopPropagation();
    const file = e.target.files[0];
    if (!file || !workflow) return;
    setSelectedWorkflowID(workflow.workflow_id);
    setSelectedWorkflowName(workflow.workflow_name);
    setSelectedFile(file);
    setManualData([]);
    setShowTable(true);
  };

  const fetchManualData = async (workflowId, workflowName) => {
    try {
      setLoading(true);
      const url = `${REACT_APP_API_BASE_URL}/get_fp?workflow_id=${workflowId}`;
      const response = await axios.get(url, { headers: authHeaders() });

      if (response.data?.data?.length) {
        const cleaned = [];
        const seen = new Set();

        response.data.data.forEach((item) => {
          item.skus_data.forEach((sku) => {
            const key = `${item.level5}-${sku.sku}-${sku.week}`;
            if (!seen.has(key)) {
              seen.add(key);
              cleaned.push({
                workflow_name: workflowName,
                sku: sku.sku,
                week: sku.week,
                forecast: String(sku.forecast),
              });
            }
          });
        });

        const pivoted = {};
        const weekSet = new Set();

        cleaned.forEach((row) => {
          const key = `${row.workflow_name}-${row.sku}`;
          weekSet.add(row.week);
          if (!pivoted[key]) {
            pivoted[key] = { workflow_name: row.workflow_name, sku: row.sku };
          }
          pivoted[key][row.week] = row.forecast;
        });

        const sortedWeeks = Array.from(weekSet).sort((a, b) => {
          const [m1, y1] = a.split("-");
          const [m2, y2] = b.split("-");
          return new Date(`${y1}-${m1}-01`) - new Date(`${y2}-${m2}-01`);
        });

        const orderedRows = Object.values(pivoted).map((row) => {
          const ordered = { sku: row.sku };
          sortedWeeks.forEach((week) => {
            ordered[week] = row[week] || "";
          });
          return ordered;
        });

        setManualData(orderedRows);
        setSelectedFile(null);
        setShowTable(true);
      } else {
        setManualData([]);
        setShowTable(true);
      }
    } catch (error) {
      console.error("Error fetching manual data:", error.message);
    } finally {
      setLoading(false);
    }
  };

  const generateExcelFile = () => {
    if (!manualData.length) return null;

    const weeks = Array.from(
      manualData.reduce((set, row) => {
        Object.keys(row).forEach((key) => {
          if (/\d{2}-\d{4}/.test(key)) set.add(key);
        });
        return set;
      }, new Set())
    ).sort();

    const rows = manualData.map((row) => {
      const rowData = { sku: row.sku };
      weeks.forEach((week) => {
        rowData[week] = row[week] ? Number(row[week]) : null;
      });
      return rowData;
    });

    const ws = XLSX.utils.json_to_sheet(rows, { header: ["sku", ...weeks] });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Forecast");
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });

    return new File([excelBuffer], "forecast.xlsx", {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
  };

  const handleSubmit = async () => {
    try {
      let fileToUpload = selectedFile;
      if (!selectedFile && manualData.length > 0) {
        fileToUpload = generateExcelFile();
      }
      if (!fileToUpload) {
        displayMessage("warning", "Missing Data", "Please select a workflow and provide data!");
        return;
      }

      const url =
        `${REACT_APP_API_BASE_URL}/upload_forecast` +
        `?user_id=${encodeURIComponent(getCookie("sub"))}` +
        `&workflow_id=${encodeURIComponent(selectedWorkflowID)}` +
        `&workflow_name=${encodeURIComponent(selectedWorkflowName)}`;

      const formData = new FormData();
      formData.append("file", fileToUpload);

      const response = await axios.post(url, formData, {
        headers: authHeaders(true),
      });

      if (response.status === 200) {
        displayMessage("success", response.data.message, "");
      } else {
        displayMessage("danger", response.data.message, "");
      }
    } catch (error) {
      console.error("Error uploading file:", error);
      displayMessage("danger", "Upload Failed", "An error occurred while uploading the file.");
    }
  };

  const getVisiblePages = () => {
    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    if (safePage <= 3) return [1, 2, 3, 4, totalPages];
    if (safePage >= totalPages - 2) {
      return [1, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
    }
    return [1, safePage - 1, safePage, safePage + 1, totalPages];
  };

  const hasData = showTable && (selectedFile || manualData.length > 0);

  return (
    <div className="w-full font-[Poppins,sans-serif]">
      <style>{`
        .up-wrap { border: 2px solid #032B4E; border-radius: 10px; overflow: hidden; background: #032B4E; }
        .up-table { width: 100%; table-layout: fixed; border-collapse: collapse; background: #fff; }
        .up-th { padding: 8px 10px; font-size: 12px; font-weight: 700; color: #fff; background: #032B4E; border: 1px solid #032B4E; text-align: left; }
        .up-th-c { text-align: center; }
        .up-td { padding: 5px 8px; font-size: 12px; color: #6a7d8c; border: 1px solid #e2eaf0; vertical-align: middle; }
        .up-td-name { color: #0c3c54; font-weight: 600; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .up-td-c { text-align: center; }
        .up-row { cursor: pointer; }
        .up-row:nth-child(even) { background: #fafbfc; }
        .up-row.is-selected { background: #fff7ed; box-shadow: inset 3px 0 0 #E06639; }
        .up-file { display: inline-flex; align-items: center; gap: 8px; }
        .up-file-btn {
          display: inline-flex; align-items: center; height: 26px; padding: 0 10px;
          border: none; border-radius: 5px; background: #E06639; color: #ffffff !important;
          font-size: 11px; font-weight: 400; cursor: pointer; font-family: Poppins, sans-serif;
          line-height: 1; white-space: nowrap;
        }
        .up-file-btn span { color: #ffffff !important; font-weight: 400; font-size: 11px; }
        .up-file-name { font-size: 10px; color: #6a7d8c; max-width: 110px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; margin-left: 2px; }
        .up-file-name.active { color: #0c3c54; font-weight: 500; }
        .up-badge {
          display: inline-flex; align-items: center; justify-content: center;
          min-width: 72px; height: 22px; padding: 0 10px; border-radius: 999px;
          font-size: 10px; font-weight: 500; color: #fff; text-transform: uppercase;
        }
        .up-badge.select { background: #6b7280; }
        .up-badge.selected { background: #E06639; }
        .up-title { margin: 0 0 12px; padding-bottom: 10px; border-bottom: 1px solid #e6eef4; font-size: 16px; font-weight: 700; color: #0c3c54; }
        .up-search {
          width: 100%; max-width: 360px; margin: 0 0 12px; padding: 8px 12px;
          border: 1px solid #e6eef4; border-radius: 8px; background: #fff;
          color: #0c3c54; font-size: 13px; font-family: Poppins, sans-serif; outline: none;
        }
        .up-search:focus { border-color: #032B4E; }
        .up-page { display: inline-flex; align-items: center; justify-content: center; min-width: 34px; padding: 6px 10px; margin: 0 3px; border: 1px solid #e6eef4; border-radius: 8px; background: #fff; color: #0c3c54; font-size: 12px; font-weight: 600; cursor: pointer; }
        .up-page.active { background: #E06639; border-color: #E06639; color: #fff; }
        .up-page:disabled { opacity: 0.5; cursor: not-allowed; }
        .up-upload {
          display: inline-flex; align-items: center; gap: 8px; padding: 8px 18px;
          border: none; border-radius: 8px; background: #E06639; color: #fff;
          font-size: 13px; font-weight: 600; cursor: pointer; font-family: Poppins, sans-serif;
        }
      `}</style>

      <PageHeader
        title="SHhare Your Inputs"
        subtitle="Select a workflow from the table and upload or review forecast data before submitting."
        style={{ marginBottom: 14, paddingBottom: 12 }}
      />

      <div className="mb-3">
        <h2 className="up-title">Select Workflow</h2>
        <input
          type="text"
          className="up-search"
          placeholder="Search by workflow name..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setCurrentPage(1);
          }}
        />
        <div className="up-wrap">
          <div className="overflow-x-auto bg-white">
            <table className="up-table">
              <thead>
                <tr>
                  <th className="up-th" style={{ width: "52%" }}>Workflow Name</th>
                  <th className="up-th" style={{ width: "28%" }}>Select File</th>
                  <th className="up-th up-th-c" style={{ width: "20%" }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {pageWorkflows.length > 0 ? (
                  pageWorkflows.map((workflow) => {
                    const isSelected = selectedWorkflowID === workflow.workflow_id;
                    return (
                      <tr
                        key={workflow.workflow_id || workflow.workflow_name}
                        className={`up-row${isSelected ? " is-selected" : ""}`}
                        onClick={() => selectWorkflow(workflow)}
                      >
                        <td className="up-td up-td-name" title={workflow.workflow_name || "-"}>
                          {workflow.workflow_name || "-"}
                        </td>
                        <td className="up-td" onClick={(e) => e.stopPropagation()}>
                          <div className="up-file">
                            <label className="up-file-btn">
                              <span>Choose File</span>
                              <input
                                type="file"
                                accept=".xlsx, .xls"
                                className="hidden"
                                onChange={(e) => handleFileChange(e, workflow)}
                              />
                            </label>
                            <span
                              className={`up-file-name${isSelected && selectedFile ? " active" : ""}`}
                            >
                              {isSelected && selectedFile ? selectedFile.name : "No file chosen"}
                            </span>
                          </div>
                        </td>
                        <td className="up-td up-td-c">
                          <span className={`up-badge ${isSelected ? "selected" : "select"}`}>
                            {isSelected ? "Selected" : "Select"}
                          </span>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td className="up-td up-td-c py-7 text-[#6a7d8c]" colSpan="3">
                      No workflows found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {filteredWorkflows.length > ROWS_PER_PAGE && (
          <div className="mt-3 flex flex-wrap items-center justify-center">
            <button
              type="button"
              className="up-page"
              disabled={safePage === 1}
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </button>
            {getVisiblePages().map((pageNum) => (
              <button
                type="button"
                key={pageNum}
                className={`up-page${safePage === pageNum ? " active" : ""}`}
                onClick={() => setCurrentPage(pageNum)}
              >
                {pageNum}
              </button>
            ))}
            <button
              type="button"
              className="up-page"
              disabled={safePage === totalPages}
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            >
              Next
            </button>
          </div>
        )}
      </div>

      {showTable && selectedFile && (
        <div className="mb-3">
          <h2 className="up-title">File Preview — {selectedWorkflowName || "Selected Workflow"}</h2>
          <div className="up-wrap bg-white overflow-x-auto">
            <DisplayExcel file={selectedFile} />
          </div>
        </div>
      )}

      {showTable && !selectedFile && (
        <div className="mb-3">
          <h2 className="up-title">Workflow Data — {selectedWorkflowName || "Selected Workflow"}</h2>
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader size="medium" />
            </div>
          ) : manualData.length > 0 ? (
            <div className="up-wrap bg-white overflow-x-auto">
              <DisplayExcel data={manualData} onEdit={setManualData} />
            </div>
          ) : (
            <p className="text-center text-sm text-[#6a7d8c] py-7">
              No forecast data found for this workflow.
            </p>
          )}
        </div>
      )}

      {hasData && (
        <div className="flex justify-end mt-3">
          <button type="button" className="up-upload" onClick={handleSubmit}>
            <FaCloudUploadAlt size={14} />
            Upload
          </button>
        </div>
      )}
    </div>
  );
};

export default UploadFile;